import { X } from 'lucide-react';

import { cn } from '@rocco/utils/cn';
import { Drawer, DrawerClose, DrawerContent } from '@rocco/components/drawer';
import { DrawerTitle, DrawerDescription } from '@rocco/components/drawer';
import type { CartItem } from '~/modules/ecommerce/type/cart-item';
import { WHITE_DISC } from '~/ui-rocco/pages/detail-shared';
import { DrawerCartPanel } from '~/ui-rocco/pages/details-page/revamp/sections/purchase/DrawerCartPanel';

interface CartDrawerProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    items: CartItem[];
    subtotal?: string | null;
}

/**
 * The cart, from anywhere.
 *
 * The same panel the product drawer slides to after an add, in the same
 * floating shell — so the cart a reader opens from the header is the cart they
 * were just shown, rather than a second design of the same list.
 */
export const CartDrawer = ({
    open,
    onOpenChange,
    items,
    subtotal,
}: CartDrawerProps) => (
    <Drawer
        direction="right"
        handleOnly
        open={open}
        onOpenChange={onOpenChange}
    >
        <DrawerContent
            noHandle
            scrollable
            hideDefaultCloseButton
            style={{ width: '25.875rem', maxWidth: '25.875rem' }}
            className={cn(
                'data-[state=open]:animate-in data-[state=open]:duration-300 data-[state=open]:slide-in-from-right',
                'data-[state=closed]:animate-out data-[state=closed]:duration-200 data-[state=closed]:slide-out-to-right',
                // The product drawer's own shell: floating, rounded, held off
                // three edges.
                'overflow-hidden rounded-[1.5rem]',
                'data-[vaul-drawer-direction=right]:inset-y-6 data-[vaul-drawer-direction=right]:right-6',
            )}
        >
            <DrawerTitle className="sr-only">Shopping cart</DrawerTitle>
            <DrawerDescription className="hidden" aria-hidden />
            <DrawerClose
                aria-label="Close"
                className={cn(WHITE_DISC, 'absolute top-3 left-3 z-20')}
            >
                <X className="size-[1.25rem]" />
            </DrawerClose>
            <DrawerCartPanel
                items={items}
                subtotal={subtotal}
                onClose={() => onOpenChange(false)}
            />
        </DrawerContent>
    </Drawer>
);
